import React, { useState, useEffect, useMemo } from 'react';
import { salesAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Sparkles, TrendingUp, Award, Filter } from 'lucide-react';
import { toast } from 'sonner';

// Données complètes avec région et secteurs
const allAgences = [
  { nom: 'Team Conti', ville: 'Lyon', region: 'Auvergne-Rhône-Alpes', caNum: 5860000, objectif: '+18.5%', secteurs: { Auto: 2100, IARD: 1800, Habitation: 1200, Vie: 760 } },
  { nom: 'Zana Messaoudi', ville: 'Paris', region: 'Île-de-France', caNum: 5280000, objectif: '+16.2%', secteurs: { Auto: 1900, IARD: 1600, Habitation: 1100, Vie: 680 } },
  { nom: 'Nawel Zlitni', ville: 'Marseille', region: 'Provence-Alpes-Côte d\'Azur', caNum: 4180000, objectif: '+12.8%', secteurs: { Auto: 1500, IARD: 1300, Habitation: 880, Vie: 500 } },
  { nom: 'Carthago', ville: 'Bordeaux', region: 'Nouvelle-Aquitaine', caNum: 3920000, objectif: '+8.6%', secteurs: { Auto: 1400, IARD: 1200, Habitation: 820, Vie: 500 } },
  { nom: 'Besoin Reel', ville: 'Toulouse', region: 'Occitanie', caNum: 3860000, objectif: '+7.5%', secteurs: { Auto: 1380, IARD: 1180, Habitation: 800, Vie: 500 } },
  { nom: 'Safaa Sud', ville: 'Nice', region: 'Provence-Alpes-Côte d\'Azur', caNum: 3680000, objectif: '+6.3%', secteurs: { Auto: 1320, IARD: 1100, Habitation: 760, Vie: 500 } },
  { nom: 'Alliance Nord', ville: 'Lille', region: 'Hauts-de-France', caNum: 3420000, objectif: '+5.1%', secteurs: { Auto: 1220, IARD: 1050, Habitation: 700, Vie: 450 } },
  { nom: 'Bretagne Assur', ville: 'Rennes', region: 'Bretagne', caNum: 3150000, objectif: '+4.2%', secteurs: { Auto: 1130, IARD: 960, Habitation: 640, Vie: 420 } },
  { nom: 'Grand Est Prime', ville: 'Strasbourg', region: 'Grand Est', caNum: 2980000, objectif: '+3.8%', secteurs: { Auto: 1060, IARD: 900, Habitation: 620, Vie: 400 } },
  { nom: 'Normandie Protect', ville: 'Rouen', region: 'Normandie', caNum: 2760000, objectif: '+2.9%', secteurs: { Auto: 980, IARD: 840, Habitation: 580, Vie: 360 } },
];

const allRegions = ['Toutes les régions', 'Île-de-France', 'Auvergne-Rhône-Alpes', 'Provence-Alpes-Côte d\'Azur', 'Nouvelle-Aquitaine', 'Occitanie', 'Hauts-de-France', 'Bretagne', 'Grand Est', 'Normandie'];
const allSecteurs = ['Tous les secteurs', 'Auto', 'IARD', 'Habitation', 'Vie'];

const ventesParRegion = {
  'Toutes les régions': [
    { mois: 'Jan', ventes: 8200, objectif: 9000 },
    { mois: 'Fév', ventes: 8800, objectif: 9200 },
    { mois: 'Mar', ventes: 9500, objectif: 9400 },
    { mois: 'Avr', ventes: 10100, objectif: 9800 },
    { mois: 'Mai', ventes: 10800, objectif: 10200 },
    { mois: 'Jun', ventes: 11300, objectif: 10600 }
  ],
  'Île-de-France': [
    { mois: 'Jan', ventes: 2800, objectif: 3000 },
    { mois: 'Fév', ventes: 3100, objectif: 3100 },
    { mois: 'Mar', ventes: 3400, objectif: 3200 },
    { mois: 'Avr', ventes: 3500, objectif: 3300 },
    { mois: 'Mai', ventes: 3700, objectif: 3400 },
    { mois: 'Jun', ventes: 3900, objectif: 3500 }
  ],
};

export const SalesIntelligence = () => {
  const [analytics, setAnalytics] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState('Toutes les régions');
  const [selectedSecteur, setSelectedSecteur] = useState('Tous les secteurs');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, recsRes] = await Promise.all([
        salesAPI.getAnalytics(),
        salesAPI.getRecommendations()
      ]);
      setAnalytics(analyticsRes.data);
      setRecommendations(recsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const generateRecommendations = async () => {
    setGenerating(true);
    try {
      const response = await salesAPI.generateRecommendations();
      setRecommendations(response.data.recommendations);
      toast.success('Recommandations IA générées avec succès!');
    } catch (error) {
      toast.error('Erreur lors de la génération des recommandations');
    } finally {
      setGenerating(false);
    }
  };

  // Filtrer les agences par région
  const filteredAgences = useMemo(() => {
    let agences = allAgences;
    if (selectedRegion !== 'Toutes les régions') {
      agences = agences.filter(a => a.region === selectedRegion);
    }
    return agences.map(a => {
      const caForSecteur = selectedSecteur !== 'Tous les secteurs'
        ? a.secteurs[selectedSecteur] * 1000
        : a.caNum;
      return {
        ...a,
        CA: caForSecteur >= 1000000 ? `${(caForSecteur / 1000000).toFixed(2)}M ` : `${(caForSecteur / 1000).toFixed(0)}K `
      };
    }).sort((a, b) => {
      const aVal = selectedSecteur !== 'Tous les secteurs' ? a.secteurs[selectedSecteur] : a.caNum;
      const bVal = selectedSecteur !== 'Tous les secteurs' ? b.secteurs[selectedSecteur] : b.caNum;
      return bVal - aVal;
    });
  }, [selectedRegion, selectedSecteur]);

  // Données graphique Ventes vs Objectifs adaptées
  const ventesObjectifsData = useMemo(() => {
    if (selectedRegion !== 'Toutes les régions' && ventesParRegion[selectedRegion]) {
      return ventesParRegion[selectedRegion];
    }
    if (selectedRegion !== 'Toutes les régions') {
      const factor = filteredAgences.length / allAgences.length || 0.15;
      return ventesParRegion['Toutes les régions'].map(d => ({
        mois: d.mois,
        ventes: Math.round(d.ventes * factor),
        objectif: Math.round(d.objectif * factor)
      }));
    }
    return ventesParRegion['Toutes les régions'];
  }, [selectedRegion, filteredAgences.length]);

  // Performance par secteur adaptée
  const performanceSecteur = useMemo(() => {
    const secteurs = ['Auto', 'IARD', 'Habitation', 'Vie'];
    const source = selectedRegion !== 'Toutes les régions'
      ? allAgences.filter(a => a.region === selectedRegion)
      : allAgences;

    if (selectedSecteur !== 'Tous les secteurs') {
      return [{ secteur: selectedSecteur, montant: source.reduce((s, a) => s + (a.secteurs[selectedSecteur] || 0), 0) }];
    }
    return secteurs.map(s => ({
      secteur: s,
      montant: source.reduce((sum, a) => sum + (a.secteurs[s] || 0), 0)
    }));
  }, [selectedRegion, selectedSecteur]);

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-muted-foreground">Chargement...</div></div>;
  }

  // Recommandations IA prédéfinies
  const recommendationsIA = [
    {
      priorite: 'Haute',
      titre: 'Développer Assurance Santé à Rennes',
      description: 'Analyse des points de vente qui ont généré une croissance de la souscription. Augmentation potentielle : 142K EUR.',
      action: '→ Lancer campagne ciblée T1',
      color: 'bg-cyan-50 border-cyan-200'
    },
    {
      priorite: 'Moyenne',
      titre: 'Optimiser Tarification Auto à Paris',
      description: 'Taux de conversion : 31%. Le programme optimal nécessite un ajustement commercial.',
      action: '→ Revoir prix et prescription',
      color: 'bg-orange-50 border-orange-200'
    },
    {
      priorite: 'Haute',
      titre: 'Opportunité Vente Croisée',
      description: 'Potentiel identifié pour des packs Habitation + Auto. Connexions potentielles : 58 clients.',
      action: '→ Proposer des paquets combinés',
      color: 'bg-green-50 border-green-200'
    }
  ];

  const rankIcons = ['', '', '', '', '', '', '', '', '', ''];

  return (
    <div className="p-8 space-y-6" data-testid="sales-intelligence-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#1E7C8C' }}>
              Performance Commerciale
            </h1>
            <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-200 px-2 py-0.5 text-xs">
              Prédictions IA
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-1">KPI Suivi des ventes - Opportunités de croissance</p>
        </div>
        <div className="flex items-center gap-3">
          <Filter size={16} className="text-muted-foreground" />
          <select
            data-testid="filter-region"
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.target.value)}
            className="text-sm border border-border rounded-lg px-3 py-2 bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            {allRegions.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <select
            data-testid="filter-secteur"
            value={selectedSecteur}
            onChange={(e) => setSelectedSecteur(e.target.value)}
            className="text-sm border border-border rounded-lg px-3 py-2 bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            {allSecteurs.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Ventes vs Objectifs */}
      <Card data-testid="ventes-objectifs-chart">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Ventes vs Objectifs</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={ventesObjectifsData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="mois" style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px' }} />
              <YAxis style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11px' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px'
                }}
              />
              <Line type="monotone" dataKey="ventes" stroke="#1E7C8C" strokeWidth={3} dot={{ fill: '#1E7C8C', r: 5 }} name="Ventes Réelles" />
              <Line type="monotone" dataKey="objectif" stroke="#94A3B8" strokeWidth={2} strokeDasharray="5 5" dot={{ fill: '#94A3B8', r: 4 }} name="Objectif" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Agences Top Performances */}
        <Card data-testid="top-agences">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Agences Top Performances</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {filteredAgences.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Aucune agence pour cette sélection</p>
            ) : filteredAgences.map((agence, index) => (
              <div key={agence.nom} className="flex items-center gap-3 p-3 bg-secondary/20 rounded-lg hover:bg-secondary/40 transition-colors">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">{index + 1}</div>
                <div className="flex-1">
                  <p className="font-semibold text-sm">{agence.nom}</p>
                  <p className="text-xs text-muted-foreground">{agence.ville} - {agence.region}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-sm data-cell">{agence.CA}</p>
                  <p className="text-xs text-green-600 font-medium">{agence.objectif}</p>
                </div>
                <Badge variant="outline" className="text-xs">
                  {index < 2 ? '100%' : index < 4 ? '95%' : '89%'}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Performance par Secteur */}
        <Card data-testid="performance-secteur">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Performance par Secteur</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={performanceSecteur} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '11px' }} />
                <YAxis dataKey="secteur" type="category" width={100} style={{ fontFamily: 'Inter, sans-serif', fontSize: '12px' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Bar dataKey="montant" fill="#1E7C8C" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Recommandations IA */}
      <Card data-testid="recommendations-ia" className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950 dark:to-cyan-950">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
                <Sparkles className="text-white" size={18} />
              </div>
              <CardTitle className="text-lg">Recommandations IA 🤖</CardTitle>
            </div>
            <Button onClick={generateRecommendations} disabled={generating} size="sm" variant="outline">
              {generating ? 'Génération...' : 'Actualiser'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {recommendationsIA.map((rec, index) => (
              <div key={index} className={`p-4 rounded-lg border-2 ${rec.color} bg-white/80 dark:bg-gray-900/80`}>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={rec.priorite === 'Haute' ? 'bg-cyan-100 text-cyan-800' : 'bg-orange-100 text-orange-800'}>
                    {rec.priorite}
                  </Badge>
                </div>
                <h4 className="font-bold text-sm mb-2">{rec.titre}</h4>
                <p className="text-xs text-muted-foreground mb-3 leading-relaxed">{rec.description}</p>
                <div className="flex items-center gap-1 text-xs font-medium text-cyan-700">
                  <TrendingUp size={14} />
                  <span>{rec.action}</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
